/**
 * İşlem kaydı — kim, ne zaman, hangi faturaya ne yaptı. Yalnızca ADMIN.
 *
 * Neden var: panelde müşterinin TCKN/VKN'si, adresi ve e-postası görünür.
 * KVKK açısından bu bir kişisel veri erişimidir ve "kim gördü" sorusunun
 * cevabı bulunabilir olmalı. Fatura görüntüleme bile kaydedilir.
 *
 * Kayıtlar **salt okunurdur** — panelden silinemez. Silinebilir bir denetim
 * kaydı denetim kaydı değildir.
 */

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAdresParametreleri } from '../lib/adres';
import type { ColumnDef } from '@tanstack/react-table';
import { RotateCw, ScrollText } from 'lucide-react';
import { api, type DenetimKaydi as DenetimKaydiTipi } from '../lib/api';
import { Kabuk } from '../components/Kabuk';
import { Kart } from '../components/ui/Kart';
import { Dugme } from '../components/ui/Dugme';
import { Secim } from '../components/ui/Girdi';
import { HataKutusu } from '../components/Durumlar';
import { VeriTablosu, KartAlani } from '../components/VeriTablosu';
import { goreliZaman, tarihSaatYaz } from '../lib/format';
import { cn } from '../lib/cn';
import { sayfaBilgisi } from '../lib/sayfa';

const EYLEMLER = [
  { deger: '', etiket: 'Tüm işlemler' },
  { deger: 'LOGIN', etiket: 'Giriş' },
  { deger: 'LOGIN_FAILED', etiket: 'Başarısız giriş' },
  { deger: 'LOGOUT', etiket: 'Çıkış' },
  { deger: 'VIEW_INVOICE', etiket: 'Fatura görüntüleme' },
  { deger: 'DOWNLOAD_PDF', etiket: 'PDF indirme' },
  { deger: 'DOWNLOAD_XML', etiket: 'XML indirme' },
  { deger: 'RETRY_INVOICE', etiket: 'Fatura tekrar gönderme' },
  { deger: 'USER_CREATED', etiket: 'Kullanıcı ekleme' },
  { deger: 'USER_UPDATED', etiket: 'Kullanıcı değiştirme' },
  { deger: 'USER_PASSWORD_RESET', etiket: 'Şifre sıfırlama' },
];

/** Dikkat çekmesi gereken eylemler kırmızı okunur. */
function eylemTonu(eylem: string): string {
  if (eylem === 'LOGIN_FAILED') return 'text-[var(--state-bad)]';
  if (eylem.startsWith('USER_') || eylem === 'RETRY_INVOICE') return 'text-[var(--state-info)]';
  return 'text-[var(--ink)]';
}

export function DenetimKaydi() {
  const { parametreler, setParametreler, adresiGuncelle } = useAdresParametreleri();
  const eylem = parametreler.get('action') ?? '';
  const sayfaNo = Math.max(1, Number(parametreler.get('page') ?? '1') || 1);
  const sayfaBoyu = Number(parametreler.get('pageSize') ?? '50') || 50;

  const { data, error, isPending, isFetching, refetch } = useQuery({
    queryKey: ['denetim', { eylem, sayfaNo, sayfaBoyu }],
    // exactOptionalPropertyTypes: `action: undefined` yazmak yerine alanı hiç
    // koymuyoruz — "belirtilmedi" ile "undefined verildi" aynı şey değil.
    queryFn: () => api.denetim({ page: sayfaNo, pageSize: sayfaBoyu, ...(eylem ? { action: eylem } : {}) }),
    placeholderData: (onceki) => onceki,
  });

  const kolonlar = useMemo<ColumnDef<DenetimKaydiTipi, unknown>[]>(
    () => [
      {
        id: 'createdAt',
        header: 'Zaman',
        enableSorting: false,
        cell: ({ row }) => (
          <div className="flex flex-col whitespace-nowrap">
            <span className="rakam text-sm text-[var(--ink)]">{tarihSaatYaz(row.original.createdAt)}</span>
            <span className="text-xs text-[var(--ink-faint)]">{goreliZaman(row.original.createdAt)}</span>
          </div>
        ),
      },
      {
        id: 'user',
        header: 'Kim',
        enableSorting: false,
        cell: ({ row }) => (
          <div className="flex min-w-0 flex-col">
            <span className="truncate text-sm text-[var(--ink)]">
              {row.original.userName ?? 'Sistem / bilinmeyen'}
            </span>
            {row.original.userEmail && (
              <span className="truncate text-xs text-[var(--ink-faint)]">{row.original.userEmail}</span>
            )}
          </div>
        ),
      },
      {
        id: 'action',
        header: 'İşlem',
        enableSorting: false,
        cell: ({ row }) => (
          <span className={cn('text-sm font-medium', eylemTonu(row.original.action))}>
            {row.original.actionLabel}
          </span>
        ),
      },
      {
        id: 'target',
        header: 'Hedef',
        enableSorting: false,
        cell: ({ row }) => (
          <span className="rakam text-sm break-all text-[var(--ink-muted)]">
            {row.original.target ?? '—'}
          </span>
        ),
      },
    ],
    []
  );

  return (
    <Kabuk
      baslik="İşlem kaydı"
      altBaslik={data ? `${data.total} kayıt · silinemez` : 'Panelde yapılan her işlem burada tutulur'}
      eylemler={
        <Dugme
          gorunum="ikincil"
          ikon={<RotateCw size={15} />}
          bekliyor={isFetching && !isPending}
          onClick={() => void refetch()}
        >
          Yenile
        </Dugme>
      }
    >
      <div className="kap uzuv p-6">
        <div className="@2xl:max-w-72">
          <Secim
            etiket="İşlem türü"
            value={eylem}
            onChange={(olay) => adresiGuncelle({ action: olay.target.value })}
          >
            {EYLEMLER.map((secenek) => (
              <option key={secenek.deger} value={secenek.deger}>
                {secenek.etiket}
              </option>
            ))}
          </Secim>
        </div>
      </div>

      {error !== null && <HataKutusu hata={error} tekrarDene={() => void refetch()} />}

      <Kart>
        <VeriTablosu
          veri={data?.items ?? []}
          kolonlar={kolonlar}
          sayfa={sayfaBilgisi(data, sayfaNo, sayfaBoyu)}
          siralama={[]}
          yukleniyor={isPending}
          tazeleniyor={isFetching && !isPending}
          satirAnahtari={(satir) => satir.id}
          onSiralama={() => undefined}
          onSayfa={(yeni) => adresiGuncelle({ page: yeni > 1 ? yeni : undefined }, false)}
          onSayfaBoyu={(boy) => adresiGuncelle({ pageSize: boy === 50 ? undefined : boy })}
          kartGoster={(satir) => (
            <div className="flex flex-col gap-2">
              <div className="flex items-start justify-between gap-2">
                <span className={cn('text-sm font-medium', eylemTonu(satir.action))}>
                  {satir.actionLabel}
                </span>
                <span className="rakam shrink-0 text-xs text-[var(--ink-faint)]">
                  {goreliZaman(satir.createdAt)}
                </span>
              </div>
              <div className="flex flex-col gap-0.5 border-t border-[var(--line)] pt-2">
                <KartAlani etiket="Kim">{satir.userName ?? 'Sistem'}</KartAlani>
                {satir.target && (
                  <KartAlani etiket="Hedef">
                    <span className="rakam">{satir.target}</span>
                  </KartAlani>
                )}
                <KartAlani etiket="Zaman">
                  <span className="rakam">{tarihSaatYaz(satir.createdAt)}</span>
                </KartAlani>
              </div>
            </div>
          )}
          bosBaslik="Bu filtreye uyan kayıt yok"
          bosAciklama="İşlem kaydı panelde yapılan her hareketi tutar. Filtreyi gevşetip tekrar deneyin."
          bosEylem={
            <Dugme
              gorunum="ikincil"
              ikon={<ScrollText size={15} />}
              onClick={() => setParametreler(new URLSearchParams())}
            >
              Tümünü göster
            </Dugme>
          }
        />
      </Kart>
    </Kabuk>
  );
}
